import { useQueryClient } from "@tanstack/react-query";
import type { FunctionComponent } from "react";
import { useCallback } from "react";

import Sidebar from "~/components/Sidebar/Sidebar";
import { STAFF_MEMBERS_ABSENCES_QUERY_KEY } from "~/modules/absence/api/absence/absenceQueries.ts";
import {
	STAFF_MEMBERS_VACATION_LEDGER_ENTRIES_QUERY_KEY,
	STAFF_MEMBERS_VACATION_LEDGER_REPORT_QUERY_KEY,
} from "~/modules/absence/api/vacationLedgerEntry/vacationLedgerEntryQueries.ts";
import {
	STAFF_MEMBERS_WORKING_SCHEDULES_QUERY_KEY,
} from "~/modules/absence/api/workingSchedule/workingScheduleQueries.ts";
import {
	ALL_STAFF_MEMBERS_QUERY_KEY,
	STAFF_MEMBER_QUERY_KEY,
} from "~/modules/humanResources/api/staffMember/staffMemberQueries.ts";
import {
	ALL_STAFF_MEMBER_EMPLOYMENTS_QUERY_KEY,
	STAFF_MEMBERS_EMPLOYMENTS_QUERY_KEY,
} from "~/modules/humanResources/api/staffMemberEmployments/staffMemberEmploymentsQueries.ts";
import type {
	StaffMemberEmploymentWithDate,
} from "~/modules/humanResources/api/staffMemberEmployments/staffMemberEmploymentsTypes.ts";
import UpdateEmploymentForm
	from "~/modules/humanResources/components/StaffMemberDetailsView/components/StaffMemberEmploymentDataSection/components/EmploymentsSection/components/UpdateEmploymentSidebar/components/UpdateEmploymentForm";
import { ALL_USERS_QUERY_KEY } from "~/modules/user/api/user/userQueries.ts";

type CreateEmploymentSidebarProps = {
	isOpen: boolean;
	onClose: () => void;
	employmentData: StaffMemberEmploymentWithDate | null;
};

const UpdateEmploymentSidebar: FunctionComponent<CreateEmploymentSidebarProps> = ({
	isOpen,
	onClose,
	employmentData,
}) => {

	const queryClient = useQueryClient();
	const handleSuccess = useCallback(async () => {
		if (!employmentData) return;
		await queryClient.refetchQueries({ queryKey: STAFF_MEMBERS_EMPLOYMENTS_QUERY_KEY(employmentData.staffMemberId) });
		await queryClient.refetchQueries({ queryKey: STAFF_MEMBER_QUERY_KEY(employmentData.staffMemberId) });
		queryClient.invalidateQueries({ queryKey: ALL_STAFF_MEMBERS_QUERY_KEY });
		queryClient.invalidateQueries({ queryKey: ALL_USERS_QUERY_KEY });
		queryClient.invalidateQueries({ queryKey: STAFF_MEMBERS_VACATION_LEDGER_ENTRIES_QUERY_KEY(employmentData.staffMemberId) });
		queryClient.invalidateQueries({ queryKey: STAFF_MEMBERS_WORKING_SCHEDULES_QUERY_KEY(employmentData.staffMemberId) });
		queryClient.invalidateQueries({ queryKey: STAFF_MEMBERS_VACATION_LEDGER_REPORT_QUERY_KEY(employmentData.staffMemberId) });
		queryClient.invalidateQueries({ queryKey: STAFF_MEMBERS_ABSENCES_QUERY_KEY(employmentData.staffMemberId) });
		queryClient.invalidateQueries({ queryKey: ALL_STAFF_MEMBER_EMPLOYMENTS_QUERY_KEY });
		onClose();
	}, [employmentData, onClose, queryClient]);

	return (
		<Sidebar closeOnOutsideClick={false}
				 open={isOpen}
				 setOpen={() => onClose()}>
			{employmentData && <UpdateEmploymentForm
				key={isOpen ? "open" : "closed"}
				onSuccess={handleSuccess}
				onCancel={onClose}
				employmentData={employmentData} />}
		</Sidebar>
	);
};

export default UpdateEmploymentSidebar;
